import { graphql } from '@predium/client-graphql';

graphql(`
  fragment DataCollectionConsumption on consumption {
    id
    provider
    to
    from
    cost
    display_unit_value
    displayValue
    created_by_user {
      first_name
      last_name
    }
    created_at
    consumption_type_id
    source_type_id
    invoice_id
    energy_source_type_id
    sub_type_id
    consumption_waste_detail {
      id
      volume
      display_unit_volume
      frequency
      display_unit_frequency
      amount
      price
    }
    consumption_areas {
      area_id
    }
  }
`);

graphql(`
  fragment DataCollectionAreaConsumptionDraft on consumption_draft {
    id
    provider
    to
    from
    cost
    display_unit_value
    displayValue
    created_by_user {
      first_name
      last_name
    }
    created_at
    consumption_type_id
    source_type_id
    energy_source_type_id
    sub_type_id
    consumption_invoice_draft {
      id
      draft_status
    }
    consumption_waste_details_draft {
      id
      volume
      display_unit_volume
      frequency
      display_unit_frequency
      amount
      price
    }
    consumption_draft_areas {
      area_id
    }
  }
`);

graphql(`
  fragment DataCollectionBuildingSubsidy on subsidy {
    id
    email
    amount_description
    provider_updated_at
    region_type
    categories {
      subsidy_category_type
    }
    title
    type
    url
    __typename
  }
`);

graphql(`
  fragment DataCollectionBuildingEnvelope on envelope {
    id
    data_source_type_id
    description
    inclination
    orientation
    envelope_type_id
    insulation_thickness
    insulation_material_name
    insulation_material_category_id
    insulation_method_id
    insulation_lambda
    base_construction_thickness
    base_construction_material_name
    base_construction_lambda
    area
    u_value
  }
`);

graphql(`
  fragment DataCollectionBuildingEnergySystemConsumerRoute on energy_system_consumer_route {
    id
    secondary_id
    area_id
    energy_final
    energy_effective
    energy_source_type_id
    energy_primary
    emission_certificate_id
    use_renewable_energy
    emission_certificate {
      ...EmissionCertificateWithProgressive
    }
    primary_energy_factor_emission_certificate_id
    primary_energy_factor_emission_certificate {
      ...PrimaryEnergyFactorCertificateWithProgressive
    }
    energy_consumer {
      id
      energy_consumer_technology_type_id
      energy_consumer_type_id
      efficiency
      construction_year
    }
    energy_distribution {
      id
      energy_distribution_type_id
      heat_distribution_loss
      heat_transfer_loss
      last_hydraulic_balancing
      last_balancing_set_to_never
      construction_year
    }
    energy_storage {
      id
      energy_storage_type_id
      annual_heat_loss
      construction_year
    }
  }
`);

graphql(`
  fragment DataCollectionBuildingEnergySystemProducerRoute on energy_system_producer_route {
    id
    area_id
    energy_effective
    energy_produced
    energy_source_type_id
    energy_producer {
      id
      name
      module_angle
      nominal_power
      construction_year
      solar_active_area
    }
  }
`);

graphql(`
  fragment DataCollectionBuildingEnergySystem on energy_system {
    id
    energy_system_type_id
    data_source_type_id
    description
    energy_system_consumer_routes(order_by: { id: asc }) {
      ...DataCollectionBuildingEnergySystemConsumerRoute
    }
    energy_system_producer_routes {
      ...DataCollectionBuildingEnergySystemProducerRoute
    }
  }
`);

graphql(`
  fragment DataCollectionBuildingModel on building_model {
    id
    description
    valid_from
    created_at
    is_draft
    processing
    energy_final
    action_planning_available
    heating_demand
    internal_temperature
    annual_heating_days
    envelope_units(order_by: { id: asc }) {
      ...DataCollectionBuildingEnvelope
    }
    energy_systems(order_by: { energy_system_type_id: asc }) {
      ...DataCollectionBuildingEnergySystem
    }
  }
`);

graphql(`
  fragment ProgressiveFactorResponse on ProgressiveFactor {
    factor
    year
  }
`);

graphql(`
  fragment EmissionFactorsPerEnergySource on EmissionFactorsObject {
    BIO_GAS {
      ...ProgressiveFactorResponse
    }
    COAL {
      ...ProgressiveFactorResponse
    }
    DISTRICT_HEATING_CHP_FOSSIL_COAL {
      ...ProgressiveFactorResponse
    }
    DISTRICT_HEATING_CHP_FOSSIL_GAS {
      ...ProgressiveFactorResponse
    }
    DISTRICT_HEATING_CHP_RENEWABLE {
      ...ProgressiveFactorResponse
    }
    DISTRICT_HEATING_PLANTS_FOSSIL_COAL {
      ...ProgressiveFactorResponse
    }
    DISTRICT_HEATING_PLANTS_FOSSIL_GAS {
      ...ProgressiveFactorResponse
    }
    ELECTRICITY_GREEN {
      ...ProgressiveFactorResponse
    }
    DISTRICT_HEATING_PLANTS_RENEWABLE {
      ...ProgressiveFactorResponse
    }
    ELECTRICITY_MIX {
      ...ProgressiveFactorResponse
    }
    WOODEN_PELLETS {
      ...ProgressiveFactorResponse
    }
    WOOD {
      ...ProgressiveFactorResponse
    }
    UNSPECIFIED {
      ...ProgressiveFactorResponse
    }
    SOLAR {
      ...ProgressiveFactorResponse
    }
    LPG {
      ...ProgressiveFactorResponse
    }
    NATURAL_GAS {
      ...ProgressiveFactorResponse
    }
    LIGNITE {
      ...ProgressiveFactorResponse
    }
    FUEL_OIL {
      ...ProgressiveFactorResponse
    }
  }
`);

graphql(`
  fragment DataCollectionBuilding on building {
    __typename
    id
    has_write_permission
    customer_external_identifier
    description
    year_constructed
    is_new_construction
    is_missing_energy_data
    year_data_source
    building_state_id
    monument_protection
    leasehold
    milieu_protection
    heritage_district
    custom_data
    address {
      id
      street
      city
      postal_code
      country_state_id
      country_id
      mapbox_building_ids
      building_citydb_gml_ids
      neighbor_citydb_gml_ids
      building_lod1_ids
      neighbor_lod1_ids
      mapbox_pitch
      mapbox_bearing
      mapbox_zoom
      latitude
      longitude
      has_gml_ids
    }
    areas(order_by: { id: asc }, where: { area_system_id: { _eq: PREDIUM_2024 } }) {
      id
      area_type_id
      area_system_id
      is_rented
      class_of_use_id
      data_source_id
      value
      type_of_use_id
      description
      external_id
      energy_certificate_id
      file_id
      geg_reference_value
      energy_rating_score
      reference_value_heating_hot_water
      reference_value_electricity
      enev_year
    }
    responsible_user {
      first_name
      last_name
    }
    energy_certificates {
      id
      energy_certificate_type
      geg_reference_value
      energy_rating_score
      co2_emissions
      reference_value_heating_hot_water
      reference_value_electricity
      enev_year
    }
    units
    units_data_source
    subsidies {
      subsidy {
        ...DataCollectionBuildingSubsidy
      }
    }
    subsidies_updated_at
    economic_unit {
      id
      name
      associatedBuildingsAndDrafts {
        amount
      }
      portfolio {
        id
        name
        __typename
      }
      __typename
    }
    active_building_model {
      ...DataCollectionBuildingModel
      efficiency_class_id
    }
    active_building_model_id
    climate_risks {
      value
      uncertainty
      type
      timespan
      created_at
    }
    climate_risks_aggregate {
      aggregate {
        max {
          created_at
        }
      }
    }
    active_action_plans_aggregate(where: { scenario: { archived_at: { _is_null: true } } }) {
      aggregate {
        count
      }
    }
    action_plans_with_action_aggregate: active_action_plans_aggregate(
      where: {
        scenario: { archived_at: { _is_null: true } }
        action_plan: { actions_aggregate: { count: { predicate: { _gt: 0 } } } }
      }
    ) {
      aggregate {
        count
      }
    }
    building_valuations {
      id
      value
      valuation_at
    }
    climate_risks_v2_aggregate(where: { api_response_status_id: { _neq: NOT_INITIALIZED } }) {
      aggregate {
        count
      }
    }
    base_emission_factors {
      ...EmissionFactorsPerEnergySource
    }
    district_heating_availability_status {
      id: building_id
      updated_at
      district_heating_status
      district_heating_year
    }
    building_models(where: { action_id: { _is_null: true } }) {
      ...DataCollectionBuildingModel
      action_id
    }
    certificates(order_by: [{ id: asc }]) {
      id
      certificate_level_type
      certificate_score
      certificate_type
      file {
        id
        name
      }
    }
  }
`);

graphql(`
  fragment DataCollectionYearlyConsumption on YearlyConsumption {
    value
    providers
    cost
    consumption_type
  }
`);

graphql(`
  fragment DataCollectionConsumptionDraftConsumptionDraft on consumption_draft {
    id
    from
    to
    displayValue
    cost
    consumption_type_id
    display_unit_value
    energy_source_type_id
    sub_type_id
    provider
    consumption_waste_details_draft {
      id
      frequency
      display_unit_frequency
      volume
      display_unit_volume
      amount
      price
    }
    consumption_draft_areas {
      area_id
    }
  }
`);

graphql(`
  fragment DataCollectionFile on file {
    __typename
    id
    name
    created_at
    downloadUrl
  }
`);

graphql(`
  fragment DataCollectionEconomicUnit on economic_unit {
    __typename
    id
    name
    customer_economic_unit_id
  }
`);

graphql(`
  fragment BuildingModelMutationAvailability on GetBuildingModelMutationAvailabilityOutput {
    affected_action_plans {
      id
      scenario_id
      scenario_name
      scenario_updated_at
    }
  }
`);

graphql(`
  fragment DataCollectionEmissionCertificateDraft on EmissionCertificateDraftWithConvertedValues {
    id
    issuer
    emission_factor_active_value_type
    primary_energy_factor_active_value_type
    emission_factor
    primary_energy_factor
    emission_certificate_energy_source_type_id
    emission_certificate_document_type_id
    valid_from
    valid_until
    emission_factor_progressive {
      year
      value
    }
    primary_energy_factor_progressive {
      year
      value
    }
    created_at
    updated_at
    org_id
    file {
      id
      name
      downloadUrl
      created_at
    }
  }
`);

graphql(`
  fragment DataCollectionEmissionCertificate on emission_certificate {
    __typename
    emission_factor_active_value_type
    primary_energy_factor_active_value_type
    created_at
    updated_at
    issuer
    emission_factor
    primary_energy_factor
    emission_certificate_value_path {
      id
      emission_factor
      primary_energy_factor
    }
    file_id
    file {
      ...DataCollectionDocumentDraft
    }
    org {
      name
    }
    org_id
    id
    valid_from
    valid_until
    emission_certificate_energy_source_type_id
    emission_certificate_document_type_id
  }
`);

graphql(`
  fragment DataCollectionDocumentDraft on file {
    downloadUrl
    id
    name
    created_at
  }
`);

graphql(`
  fragment DataCollectionEmissionCertificateListItem on EmissionCertificateListItem {
    emission_factor_active_value_type
    primary_energy_factor_active_value_type
    created_at
    issuer
    emission_factor
    primary_energy_factor
    emission_certificate_value_path {
      id
      emission_factor
      primary_energy_factor
    }
    id
    valid_until
    emission_certificate_energy_source_type_id
    associated_action_plans_count
    associated_buildings_count
  }
`);

graphql(`
  fragment EnergySupplierAssociatedBuilding on building {
    id
    address {
      street
      city
      postal_code
    }
  }
`);

export const GET_ASSOCIATED_BUILDINGS_FOR_ENERGY_SUPPLIER = graphql(`
  query getAssociatedBuildingsForEnergySupplier($emissionCertificateId: Int!, $offset: Int, $limit: Int) {
    getAssociatedBuildings(emissionCertificateId: $emissionCertificateId, offset: $offset, limit: $limit) {
      count
      buildings {
        building {
          ...EnergySupplierAssociatedBuilding
        }
      }
    }
  }
`);

export const GET_ASSOCIATED_ACTION_PLANS_FOR_ENERGY_SUPPLIER = graphql(`
  query getAssociatedActionPlansForEnergySupplier($emissionCertificateId: Int!, $offset: Int, $limit: Int) {
    getAssociatedActionPlans(emissionCertificateId: $emissionCertificateId, offset: $offset, limit: $limit) {
      count
      action_plans {
        stable_id
        scenario_id
        building {
          ...EnergySupplierAssociatedBuilding
        }
      }
    }
  }
`);

graphql(`
  fragment EmissionCertificateWithProgressive on emission_certificate {
    emission_factor
    emission_factor_active_value_type
    emission_certificate_value_path {
      emission_factor
    }
  }
`);

graphql(`
  fragment PrimaryEnergyFactorCertificateWithProgressive on emission_certificate {
    primary_energy_factor
    primary_energy_factor_active_value_type
    emission_certificate_value_path {
      primary_energy_factor
    }
  }
`);

//Economic unit ------------------------------------

export const GET_ECONOMIC_UNITS = graphql(`
  query DataCollectionGetEconomicUnits($where: economic_unit_bool_exp) {
    economic_unit(order_by: { name: asc }, where: $where) {
      id
      name
      customer_economic_unit_id
      portfolio {
        id
        name
      }
    }
  }
`);

export const GET_ECONOMIC_UNITS_BY_IDS_FOR_SELECTION = graphql(`
  query DataCollectionGetEconomicUnitsByIdsForSelection($where: economic_unit_bool_exp!) {
    economic_unit(where: $where) {
      id
      name
      responsible_user_id
    }
  }
`);

export const GET_ECONOMIC_UNITS_AGGREGATE_COUNT = graphql(`
  query DataCollectionGetEconomicUnitsAggregateCount($where: economic_unit_bool_exp!) {
    economic_unit_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_ECONOMIC_UNITS_BY_SEARCH = graphql(`
  query DataCollectionGetEconomicUnitsBySearch($limit: Int!, $offset: Int!, $where: economic_unit_bool_exp!) {
    economic_unit(order_by: { name: asc }, limit: $limit, offset: $offset, where: $where) {
      id
      name
    }
  }
`);

export const GET_ECONOMIC_UNITS_WITH_BUILDINGS = graphql(`
  query EconomicUnitsList(
    $where: BuildingWhere!
    $orderBy: EconomicUnitsOrderByInput
    $fetchInactiveBuildings: Boolean = false
    $offset: Int!
    $limit: Int!
  ) {
    getEconomicUnits(where: $where, orderBy: $orderBy, offset: $offset, limit: $limit) {
      economicUnits {
        id
        economicUnit {
          id
          name
          customer_economic_unit_id
          description
          created_at
          updated_at
          custom_data
          responsible_user {
            id
            first_name
            last_name
          }
          portfolio {
            id
            name
            has_write_permission
          }
          first_building: buildings(limit: 1) {
            id
            address {
              city
              postal_code
            }
          }
          first_inactive_building: inactive_buildings(limit: 1) @include(if: $fetchInactiveBuildings) {
            id
            address {
              city
              postal_code
            }
          }
        }
      }
      count
    }
  }
`);

export const GET_ECONOMIC_UNITS_WITH_BUILDING_WHERE = graphql(`
  query EconomicUnitsWithBuildingWhere($where: BuildingWhere!) {
    getEconomicUnits(where: $where) {
      economicUnits {
        id
      }
    }
  }
`);

export const GET_ECONOMIC_UNIT_BUILDINGS_IDS = graphql(`
  query EconomicUnitBuildingsIds($where: economic_unit_bool_exp!) {
    economic_unit(where: $where) {
      id
      name
      portfolio {
        id
        has_write_permission
      }
      buildings {
        id
      }
      inactive_buildings {
        id
      }
      building_drafts_aggregate {
        aggregate {
          count
        }
      }
    }
  }
`);

// Building ------------------------------------

export const GET_BUILDING = graphql(`
  query DataCollectionBuildingGetBuilding($buildingId: Int!) {
    building_by_pk(id: $buildingId) {
      ...DataCollectionBuilding
    }
  }
`);

export const GET_CONSUMPTIONS = graphql(`
  query DataCollectionGetBuildingConsumptions($buildingId: Int!) {
    building(where: { id: { _eq: $buildingId } }) {
      consumptions(order_by: { created_at: desc }) {
        ...DataCollectionConsumption
      }
    }
  }
`);

export const GET_CONSUMPTION_DRAFTS = graphql(`
  query DataCollectionGetBuildingConsumptionDrafts($buildingId: Int!) {
    consumption_draft(where: { building_id: { _eq: $buildingId } }, order_by: { created_at: desc }) {
      ...DataCollectionAreaConsumptionDraft
    }
  }
`);

export const GET_ECONOMIC_UNIT_ASSOCIATED_BUILDINGS_AND_DRAFTS = graphql(`
  query EconomicUnitAssociatedBuildingsAndDrafts($economicUnitId: Int!) {
    economic_unit_by_pk(id: $economicUnitId) {
      id
      associatedBuildingsAndDrafts {
        amount
      }
    }
  }
`);

export const GET_ALL_BUILDINGS_TABLE = graphql(`
  query DataCollectionGetAllBuildingsTableWithBuildingWhere(
    $buildingWhere: BuildingWhere!
    $limit: Int
    $offset: Int
    $orderBy: BuildingOrderByInput
  ) {
    getBuildingList(where: $buildingWhere, limit: $limit, offset: $offset, orderBy: $orderBy) {
      buildings {
        id
        energy_data {
          energy_final
        }
        energy_certificate_types
        types_of_use
        inactive_building {
          id
          customer_external_identifier
          monument_protection
          milieu_protection
          leasehold
          heritage_district
          state
          has_write_permission
          address {
            id
            street
            city
            postal_code
            country_id
            has_gml_ids
          }
          year_constructed
          responsible_user {
            id
            first_name
            last_name
          }
          building_valuations {
            id
            value
            valuation_at
          }
          areas(where: { area_type_id: { _eq: EBF } }) {
            id
            value
            area_type_id
          }

          economic_unit {
            id
            name
            portfolio {
              id
              name
            }
          }
          created_at
          active_building_model_id
          active_building_model {
            id
            processing
            energy_systems {
              id
              energy_system_consumer_routes {
                id
                energy_final
                energy_source_type_id
                energy_primary
              }
            }
          }
        }
        building {
          id
          customer_external_identifier
          monument_protection
          milieu_protection
          leasehold
          heritage_district
          has_write_permission
          state
          custom_data
          address {
            id
            street
            city
            postal_code
            country_id
            has_gml_ids
          }
          year_constructed
          is_new_construction
          responsible_user {
            id
            first_name
            last_name
          }
          building_valuations {
            id
            value
            valuation_at
          }
          areas(where: { area_type_id: { _eq: EBF } }) {
            id
            value
            area_type_id
          }

          economic_unit {
            id
            name
            portfolio {
              id
              name
            }
          }
          created_at
          active_building_model_id
          active_building_model {
            id
            processing
            energy_systems {
              id
              energy_system_consumer_routes {
                id
                energy_final
                energy_source_type_id
                energy_primary
              }
            }
          }
        }
        has_gml_ids
      }
      count
    }
  }
`);

graphql(`
  fragment EmissionCertificateDropdownFields on emission_certificate {
    id
    issuer
    created_at
    emission_factor
    emission_factor_active_value_type
    primary_energy_factor
    primary_energy_factor_active_value_type
    emission_certificate_energy_source_type_id
    emission_certificate_value_path {
      emission_factor
      primary_energy_factor
    }
  }
`);

export const GET_ALL_EMISSION_CERTIFICATES_DROPDOWN = graphql(`
  query DataCollectionGetAllEmissionCertificatesDropdown {
    emission_certificate {
      ...EmissionCertificateDropdownFields
    }
  }
`);

export const GET_BUILDING_EXPORT_TEMPLATES = graphql(`
  query ExportTemplates($orgId: Int) {
    export_template(where: { _or: [{ org_id: { _eq: $orgId } }, { org_id: { _is_null: true } }] }) {
      id
      name
      export_type_id
    }
  }
`);

//Sub building ------------------------------------

export const GET_ASSOCIATED_ENTITIES_BY_BUILDING_IDS = graphql(`
  query AssociatedEntitiesByBuildingId($buildingIds: [Int!]!) {
    report_building(where: { building_id: { _in: $buildingIds } }) {
      report {
        id
        progress
        report_buildings {
          building_id
        }
        report_template {
          title
        }
      }
    }

    active_action_plans_aggregate(
      where: { action_plan: { active_scenario: { action_plan: { building_id: { _in: $buildingIds } } } } }
    ) {
      aggregate {
        count
      }
    }

    action_aggregate(
      where: { action_plan: { active_scenario: { action_plan: { building_id: { _in: $buildingIds } } } } }
    ) {
      aggregate {
        count
      }
    }

    consumption_aggregate(where: { building_id: { _in: $buildingIds } }) {
      aggregate {
        count
      }
    }

    consumption_draft_aggregate(where: { building: { id: { _in: $buildingIds } } }) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_CONSUMPTION_SUMMARIES = graphql(`
  query DataCollectionGetConsumptionSummaries($buildingId: Int!, $year: Int!) {
    getYearlyConsumptionSummary(building_id: $buildingId, year: $year) {
      consumptionCategories {
        ...DataCollectionYearlyConsumption
      }
    }
  }
`);

export const GET_ALL_BUILDING_DRAFTS = graphql(`
  query GetAllBuildingDrafts(
    $where: building_draft_bool_exp!
    $orderBy: [building_draft_order_by!]!
    $limit: Int
    $offset: Int
  ) {
    building_draft(where: $where, order_by: $orderBy, limit: $limit, offset: $offset) {
      id
      street
      city
      external_id
      created_at
      custom_data
      user {
        id
        first_name
        last_name
      }
      energy_certificate_drafts {
        id
        draft_status_id
        file {
          id
          name
        }
      }
      energy_certificates {
        id
        file {
          id
          name
        }
      }
    }
    building_draft_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_PORTFOLIOS_AND_ECONOMIC_UNITS = graphql(`
  query GetAllBuildingDraftsPortfoliosAndEconomicUnits {
    portfolio(order_by: { name: asc }) {
      id
      name
    }
    economic_unit(order_by: { name: asc }) {
      id
      name
    }
  }
`);

export const GET_BUILDING_DRAFT_IDS_BY_EPC_NAME = graphql(`
  query GetBuildingDraftIdsByEpcName($nameLike: String!) {
    energy_certificate_draft(where: { file: { name: { _ilike: $nameLike } } }) {
      building_draft {
        id
      }
    }
    energy_certificate(where: { file: { name: { _ilike: $nameLike } }, building_draft: { id: { _is_null: false } } }) {
      building_draft {
        id
      }
    }
  }
`);

//Consumption  ------------------------------------

export const GET_CONSUMPTION_INVOICE = graphql(`
  query DataCollectionGetConsumptionInvoice($consumptionInvoiceId: Int!) {
    consumption_invoice_by_pk(id: $consumptionInvoiceId) {
      id
      file {
        downloadUrl
        name
      }
      date
      invoice_payer
      consumptions {
        ...DataCollectionConsumption
      }
      building {
        id
        has_write_permission
        address {
          id
          street
          city
          postal_code
        }
        areas(where: { area_type_id: { _in: [MF, AF, NGF] } }) {
          id
          external_id
          description
          area_type_id
        }
      }
    }
  }
`);

export const GET_CONSUMPTIONS_INVOICES = graphql(`
  query DataCollectionAreaConsumptionGetConsumptions($buildingId: Int! = 1) {
    consumption_invoice(where: { building_id: { _eq: $buildingId } }, order_by: { created_at: desc }) {
      id
    }
  }
`);

export const COUNT_BUILDING_CONSUMPTIONS_BY_AREA_TYPE = graphql(`
  query CountBuildingConsumptionsByAreaType($buildingId: Int!, $areaTypeId: area_type_enum!) {
    building {
      consumptions_aggregate(
        where: {
          building_id: { _eq: $buildingId }
          consumption_areas: { area: { area_type_id: { _eq: $areaTypeId } } }
        }
      ) {
        aggregate {
          count
        }
      }
    }
  }
`);

//Consumption draft ------------------------------------

export const GET_BUILDING_CONSUMPTION_PRECONDITION = graphql(`
  query DataCollectionGetBuildingConsumptionPrecondition($orgId: Int!) {
    building(where: { economic_unit: { portfolio: { org: { id: { _eq: $orgId } } } } }) {
      id
      has_write_permission
      address {
        id
        city
        street
        postal_code
      }
      building_models {
        id
        energy_systems {
          id
          energy_system_type_id
        }
      }
      areas(where: { area_type_id: { _in: [MF, AF, NGF] } }) {
        id
        external_id
        description
        area_type_id
        value
      }
      economic_unit {
        id
        portfolio {
          id
          has_write_permission
        }
      }
    }
  }
`);

export const GET_CONSUMPTION_INVOICE_DRAFT = graphql(`
  query DataCollectionGetConsumptionInvoiceDraft($consumptionInvoiceDraftId: Int!) {
    consumption_invoice_draft_by_pk(id: $consumptionInvoiceDraftId) {
      id
      file {
        downloadUrl
        name
      }
      date
      draft_status
      invoice_payer
      building {
        id
        address {
          id
          street
          city
          postal_code
        }
        areas {
          id
          external_id
          description
          area_type_id
        }
      }

      consumption_drafts {
        ...DataCollectionConsumptionDraftConsumptionDraft
      }
    }
  }
`);

// TODO PRE-789: change query to properly fetching with building_id
export const GET_CONSUMPTION_INVOICE_DRAFTS = graphql(`
  query DataCollectionAreaGetConsumptionDrafts($buildingId: Int!) {
    consumption_invoice_draft(where: { building_id: { _eq: $buildingId } }, order_by: { created_at: desc }) {
      id
    }
  }
`);

/**
 * Returns every PDF in the building's drive folder tree (BUILDING folder and all
 * descendants), annotated with the matching energy_certificate id when one exists.
 * Used to populate the area EBF form picker, including PDFs that are not (yet) linked
 * to an energy_certificate row and PDFs migrated from the pre-drive building_document system.
 */
export const GET_BUILDING_PDF_FILES = graphql(`
  query GetBuildingPdfFiles($buildingId: Int!) {
    getBuildingPdfFiles(buildingId: $buildingId) {
      files {
        id
        name
        uploading
        createdAt
        downloadUrl
        energyCertificateId
      }
    }
  }
`);

/**
 * Pre-checks if we need to show actions deletion banner or not
 */
export const GET_BUILDING_MODEL_ACTIONS_STATUS = graphql(`
  query getBuildingModelMutationAvailability($buildingModelId: Int!) {
    getBuildingModelMutationAvailability(building_model_id: $buildingModelId) {
      ...BuildingModelMutationAvailability
    }
  }
`);

/**
 * Fetches a paginated, filterable, sortable page of emission certificates
 */
export const GET_EMISSION_CERTIFICATES = graphql(`
  query getEmissionCertificates(
    $where: EmissionCertificateWhereInput
    $orderBy: EmissionCertificateOrderByInput
    $limit: Int
    $offset: Int
  ) {
    getEmissionCertificates(where: $where, orderBy: $orderBy, limit: $limit, offset: $offset) {
      certificates {
        ...DataCollectionEmissionCertificateListItem
      }
      count
    }
  }
`);

/**
 * Fetches per-status counts (all/valid/expired) for the Energy Suppliers tab bar
 */
export const GET_EMISSION_CERTIFICATE_TAB_COUNTS = graphql(`
  query getEmissionCertificateTabCounts($now: timestamptz!) {
    all: emission_certificate_aggregate {
      aggregate {
        count
      }
    }
    valid: emission_certificate_aggregate(
      where: { _or: [{ valid_until: { _gte: $now } }, { valid_until: { _is_null: true } }] }
    ) {
      aggregate {
        count
      }
    }
    expired: emission_certificate_aggregate(where: { valid_until: { _lt: $now } }) {
      aggregate {
        count
      }
    }
  }
`);

/**
 * Fetches a specific emission certificate by id
 */
export const GET_EMISSION_CERTIFICATE = graphql(`
  query getEmissionCertificate($emissionCertificateId: Int!) {
    emission_certificate_by_pk(id: $emissionCertificateId) {
      ...DataCollectionEmissionCertificate
    }
  }
`);

/**
 * Fetches a paginated, filterable, sortable page of emission certificate drafts
 */
export const GET_EMISSION_CERTIFICATE_DRAFTS = graphql(`
  query getEmissionCertificateDrafts(
    $where: EmissionCertificateDraftWhereInput
    $orderBy: EmissionCertificateDraftOrderByInput
    $limit: Int
    $offset: Int
  ) {
    getEmissionCertificateDrafts(where: $where, orderBy: $orderBy, limit: $limit, offset: $offset) {
      drafts {
        ...DataCollectionEmissionCertificateDraft
      }
      count
    }
  }
`);

/**
 * Fetches an emission certificate draft by id
 */
export const GET_EMISSION_CERTIFICATE_DRAFT = graphql(`
  query getEmissionCertificateDraft($emissionCertificateDraftId: Int!) {
    getEmissionCertificateDraft(id: $emissionCertificateDraftId) {
      ...DataCollectionEmissionCertificateDraft
    }
  }
`);

/**
 * Fetches an emission certificate draft by file_id
 */
export const GET_EMISSION_CERTIFICATE_DRAFT_BY_FILE = graphql(`
  query getEmissionCertificateDraftByFile($emissionCertificateDraftFileId: Int!) {
    emission_certificate_draft(where: { file_id: { _eq: $emissionCertificateDraftFileId } }) {
      id
    }
  }
`);

/**
 *  Fetches all associated action plans and reports with the building
 */
export const GET_ASSOCIATED_ACTION_PLANS_AND_REPORTS_FROM_BUILDING = graphql(`
  query getAssociatedActionPlansAndReportsFromEmissionCertificate($emissionCertificateId: Int!) {
    emission_factor_buildings: building(
      where: {
        building_models: {
          energy_systems: {
            energy_system_consumer_routes: { emission_certificate_id: { _eq: $emissionCertificateId } }
          }
        }
      }
    ) {
      id
      active_action_plans_aggregate {
        aggregate {
          count
        }
      }
      report_buildings {
        report_id
      }
    }
    primary_energy_factor_buildings: building(
      where: {
        building_models: {
          energy_systems: {
            energy_system_consumer_routes: {
              primary_energy_factor_emission_certificate_id: { _eq: $emissionCertificateId }
            }
          }
        }
      }
    ) {
      id
      active_action_plans_aggregate {
        aggregate {
          count
        }
      }
      report_buildings {
        report_id
      }
    }
  }
`);

export const BUILDING_MODEL_SUBSCRIPTION = graphql(`
  subscription BuildingModelSubscription($buildingModelId: Int!) {
    building_model_by_pk(id: $buildingModelId) {
      ...DataCollectionBuildingModel
    }
  }
`);

export const BUILDING_PROCESSING_SUBSCRIPTION = graphql(`
  subscription BuildingProcessingSubscription($buildingModelId: Int!) {
    building_model_by_pk(id: $buildingModelId) {
      id
      processing
    }
  }
`);

export const IS_LAST_EU_FROM_BUILDINGS = graphql(`
  query hasSingleBuildingOrDraft($ids: [Int!]!) {
    hasSingleBuildingOrDraft(ids: $ids)
  }
`);

export const USER_HAS_WRITE_ACCESS_TO_ALL_BUILDINGS = graphql(`
  query UserHasWriteAccessToAllBuildings($buildingWhere: BuildingWhere!) {
    getBuildingList(where: $buildingWhere, checkAccess: WRITE) {
      count
    }
  }
`);

export const GET_BUILDINGS_BY_ECONOMIC_UNIT_ID = graphql(`
  query GetBuildingsByEconomicUnitId($economicUnitId: Int!) {
    building(where: { economic_unit_id: { _eq: $economicUnitId } }) {
      id
      state
      has_write_permission
      building_state_id
      year_constructed
      is_new_construction
      is_missing_energy_data
      areas(where: { area_type_id: { _eq: EBF } }) {
        id
        type_of_use_id
        area_type_id
        value
      }
      address {
        id
        street
      }
    }
  }
`);

export const GET_INACTIVE_BUILDINGS_BY_ECONOMIC_UNIT_ID = graphql(`
  query GetInactiveBuildingsByEconomicUnitId($economicUnitId: Int!) {
    inactive_buildings(where: { economic_unit_id: { _eq: $economicUnitId } }) {
      id
      state
      has_write_permission
      building_state_id
      year_constructed
      areas(where: { area_type_id: { _eq: EBF } }) {
        id
        type_of_use_id
        area_type_id
        value
      }
      address {
        id
        street
      }
    }
  }
`);

export const GET_BUILDINGS_HAS_WRITE_PERMISSION = graphql(`
  query GetBuildingsHasWritePermission($buildingIds: [Int!]!) {
    building(where: { id: { _in: $buildingIds } }) {
      id
      has_write_permission
    }
  }
`);

/**
 * Minimal street-name lookup for the export config modal's building summary.
 * Uses the same `BuildingWhere` the export itself runs on, so it returns exactly
 * the exported set (including the select-all-across-pages filter). `limit` is
 * capped by the caller to the "reasonable amount" threshold — we never list
 * names for huge selections, so the fetch stays bounded.
 */
export const GET_EXPORTED_BUILDING_NAMES = graphql(`
  query GetExportedBuildingNames($buildingWhere: BuildingWhere!, $limit: Int!) {
    getBuildingList(where: $buildingWhere, limit: $limit) {
      buildings {
        id
        building {
          id
          address {
            id
            street
          }
        }
        inactive_building {
          id
          address {
            id
            street
          }
        }
      }
    }
  }
`);

export const GET_USERS = graphql(`
  query GetUsers {
    user {
      id
      first_name
      last_name
    }
  }
`);

graphql(`
  fragment EPCFragment on energy_certificate {
    __typename
    id
    updated_at
    street
    postal_code
    city
    country_id
    year_constructed
    area_usable
    type_of_use_id
    energy_certificate_type
    expiry_date
    units_commercial
    units_residential
    geg_reference_value
    energy_rating_score
    co2_emissions
    reference_value_heating_hot_water
    reference_value_electricity
    enev_year
    energy_certificate_consumers {
      id
      energy_system_type_id
      energy_source_type_id
      energy_final
    }
    file {
      id
      name
      uploading
      downloadUrl
    }
  }
`);

graphql(`
  fragment EPCDraftFragment on energy_certificate_draft {
    __typename
    id
    street
    draft_status_id
    postal_code
    city
    country_id
    country_state_id
    year_constructed
    area_usable
    type_of_use_id
    energy_certificate_type_id
    expiry_date
    units
    geg_reference_value
    energy_rating_score
    co2_emissions
    reference_value_heating_hot_water
    reference_value_electricity
    enev_year
    file {
      id
      name
      uploading
    }
    energy_certificate_consumers {
      id
      energy_system_type_id
      energy_source_type_id
      energy_final
    }
  }
`);

graphql(`
  fragment EPCDraftWithFileURLFragment on energy_certificate_draft {
    __typename
    ...EPCDraftFragment
    file {
      id
      name
      downloadUrl
      uploading
    }
  }
`);

graphql(`
  fragment GetBuildingDraftFragment on building_draft {
    id
    year_constructed
    responsible_user_id
    postal_code
    building_state_id
    city
    country_id
    economic_unit_id
    heritage_district
    monument_protection
    milieu_protection
    leasehold
    portfolio_id
    floors
    external_id
    street
    units
    latitude
    longitude
    mapbox_building_ids
    building_citydb_gml_ids
    neighbor_citydb_gml_ids
    building_lod1_ids
    neighbor_lod1_ids
    custom_data
    assign_responsible_user_as_transaction_manager
    building_draft_valuations(order_by: { id: asc }) {
      id
      value
      valuation_at
    }
    building_draft_consumers(order_by: { id: asc }) {
      id
      energy_source_type_id
      energy_final
      energy_system_type_id
      building_draft_area_id
    }
    building_draft_areas(order_by: { id: asc }) {
      id
      name
      type_of_use_id
      value
      energy_certificate_id
      geg_reference_value
      energy_rating_score
      reference_value_heating_hot_water
      reference_value_electricity
      enev_year
    }
    energy_certificate_drafts(order_by: { id: asc }) {
      ...EPCDraftWithFileURLFragment
    }
    energy_certificates(order_by: { id: asc }) {
      ...EPCFragment
    }
    certificates(order_by: { id: asc }) {
      id
      certificate_level_type
      certificate_score
      certificate_type
      file {
        id
        name
      }
    }
  }
`);

export const GET_BUILDING_DRAFT = graphql(`
  query GetBuildingDraft($id: Int!) {
    building_draft_by_pk(id: $id) {
      ...GetBuildingDraftFragment
    }
  }
`);

export const COMPLETE_BUILDING = graphql(`
  query CompleteBuilding(
    $areaUsable: Float
    $energyData: [EnergyDataInput!]!
    $typeOfUse: type_of_use_enum
    $geolocation: BuildingCompleteGeolocation
    $buildingGmlIds: [String!]
    $buildingLOD1Ids: [String!]
    $units: Int
    $year: Int
  ) {
    completeBuilding(
      areaUsable: $areaUsable
      energyData: $energyData
      geolocation: $geolocation
      buildingGmlIds: $buildingGmlIds
      buildingLOD1Ids: $buildingLOD1Ids
      typeOfUse: $typeOfUse
      units: $units
      year: $year
    ) {
      areaUsable
      areaUsableDataSource
      energyData {
        energySource
        energySystemType
        finalEnergyDemand
      }
      energyDataSource
      typeOfUse
      typeOfUseDataSource
      units
      unitsDataSource
      yearConstructed
      yearConstructedDataSource
    }
  }
`);

export const GET_BUILDING_DRAFT_COUNT = graphql(`
  query DataCollectionBuildingDraftCount {
    building_draft_aggregate {
      aggregate {
        count
      }
    }
  }
`);

export const GET_CLIMATE_RISK_V2_REPORT = graphql(`
  query GetClimateRiskV2Report($buildingId: Int!) {
    readClimateRiskV2Report(buildingId: $buildingId) {
      success
      data {
        id
        status
        createdAt
        modelVersion
        files {
          name
          path
          publicPath
        }
        scenarios {
          type
          risks {
            riskName
            periods {
              percentage
              range
              severity
              valuationAtRisk
            }
          }
        }
      }
    }
  }
`);

export const ENERGY_CERTIFICATE_DRAFTS_SUBSCRIPTION = graphql(`
  subscription EnergyCertificateDraftsSubscription($buildingDraftId: Int!) {
    energy_certificate_draft(where: { building_draft: { id: { _eq: $buildingDraftId } } }) {
      ...EPCDraftFragment
    }
  }
`);

export const PORTFOLIO_FOR_BUILDING_DRAFT = graphql(`
  query PortfolioForBuildingDraft($userId: Int!) {
    building(
      order_by: { id: desc }
      limit: 1
      where: {
        economic_unit: {
          portfolio: {
            _or: [
              { org: { users: { id: { _eq: $userId }, role_id: { _eq: ORG_ADMIN } } } }
              { user_permissions: { _and: { write: { _eq: true }, user_id: { _eq: $userId } } } }
            ]
          }
        }
      }
    ) {
      id
      economic_unit {
        portfolio {
          id
        }
      }
    }
    # In case there is no buildings, we will use the portfolio of the user
    portfolio(
      limit: 1
      where: {
        _or: [
          { org: { users: { id: { _eq: $userId }, role_id: { _eq: ORG_ADMIN } } } }
          { user_permissions: { _and: { write: { _eq: true }, user_id: { _eq: $userId } } } }
        ]
      }
    ) {
      id
    }
  }
`);

export const PORTFOLIO_FOR_MULTIPLE_BUILDING_DRAFTS = graphql(`
  query PortfolioForMultipleBuildingDrafts($userId: Int!) {
    portfolio(
      order_by: { name: asc }
      where: {
        _or: [
          { org: { users: { id: { _eq: $userId }, role_id: { _eq: ORG_ADMIN } } } }
          { user_permissions: { _and: { write: { _eq: true }, user_id: { _eq: $userId } } } }
        ]
      }
    ) {
      id
      name
      economic_units {
        id
        name
        customer_economic_unit_id
      }
    }
  }
`);

export const GET_GLB_ACCESS_COOKIES = graphql(`
  query GetGlbAccessCookies($glbPath: String) {
    getGlbAccessCookies(glbPath: $glbPath) {
      success
      signedUrl
    }
  }
`);

export const VALIDATE_BUILDING_IDS = graphql(`
  query ValidateBuildingIds($where: building_bool_exp!) {
    building(where: $where) {
      id
    }
  }
`);

export const GET_LAST_TYPE_OF_USES = graphql(`
  query GetLastTypesOfUse {
    fetchLastUsedTypesOfUse {
      data
    }
  }
`);

export const GET_CUSTOM_FIELDS_BY_ORG = graphql(`
  query GetCustomFieldsByOrg($orgId: Int!, $entityType: custom_field_entity_enum!) {
    custom_field(where: { entity_type: { _eq: $entityType }, org_id: { _eq: $orgId } }) {
      id
      name
      key
      type_id
      suffix
    }
  }
`);
